import { Select, Table, TableColumnsType } from 'antd';
import _ from 'lodash';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  createLessonPlans,
  getLessonPlans,
  updateLessonPlans,
} from '../../../api/semesterLessonPlan.api';
import {
  getSemesterCourse,
  getSemesterCourseMonths,
} from '../../../api/semesters.api';
import { getSubjects } from '../../../api/subject.api';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IGetLessonPlans,
  ILessonPlan,
} from '../../../models/semesterLessonPlan.model';
import {
  IGetSemesterCourse,
  IGetSemesterCourseMonths,
} from '../../../models/semesters.model';
import { IGetSubjects } from '../../../models/subject.model';
import Button from '../../Button';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';

const ScrollView = styled.div`
  height: calc(100vh - 300px);
  overflow-y: auto;
`;

interface IProps {
  courseInput: any;
  chosenCourseList: any;
}

const SemesterPlan: FC<IProps> = ({
  courseInput,
  chosenCourseList,
}) => {
  const { t } = useTranslation();
  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesterCourseMonths>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetSemesterCourse>();
  const { data: res3, isLoading: isLoading3, error: error3, execute: execute3 } = useAxios<IGetLessonPlans>();
  const { data: res4, isLoading: isLoading4, error: error4, execute: execute4 } = useAxios<IGetSubjects>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios<IGetLessonPlans>();

  const [courseIndex, setCourseIndex] = useState<number>();
  const [timetableSeq, setTimetableSeq] = useState<number>();
  const [courseTimesList, setCourseTimesList] = useState<any>([]);
  const [dayLessonCount, setDayLessonCount] = useState(0);
  const [planInputs, setPlanInputs] = useState<any>([]);
  const [isCreate, setIsCreate] = useState(true);

  // 학기 선택 변경 시 대응
  useEffect(() => {
    setTimetableSeq(undefined);
    setCourseIndex(undefined);
  }, [courseInput]);

  useEffect(() => {
    execute4(getSubjects());
  }, []);

  useEffect(() => {
    if (res && !isLoading) {
      const data = _.cloneDeep(res.data);
      if (!data) return;

      let prevSeq = 0;
      let times = 1;
      data.forEach((item: any) => {
        if (item.monthSeq > prevSeq) {
          times = 1;
          item.times = times;
        } else {
          item.times = ++times;
        }
        item.month = getCommonCodeLabel('SY0009', item.monthNameCode, 'ko');
        prevSeq = item.monthSeq;
      });

      setCourseTimesList(data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (res2 && !isLoading2) {
      setDayLessonCount(res2.data?.dayLessonCount);
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (res3 && !isLoading3) {
      if (!res3?.data?.length) {
        const inputs = [];
        for (let i = 1; i <= dayLessonCount; i++) {
          inputs.push({
            timetableSeq,
            classTimeSeq: i,
            subjectCode: undefined,
            lessonName: '',
          });
        }
        setPlanInputs(inputs);
        setIsCreate(true);
      } else {
        setPlanInputs(res3.data);
        setIsCreate(false);
      }
    }
  }, [res3, isLoading3]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      successNotification(t('common.message.save1'));
      setPlanInputs(submitRes.data);
      setIsCreate(false);
    }
  }, [submitRes, submitIsLoading]);

  const onSelectCourse = (index: number) => {
    setTimetableSeq(undefined);
    setCourseIndex(index);
    const semesterCourseCode = courseInput[index].id;
    execute(getSemesterCourseMonths(semesterCourseCode));
    execute2(getSemesterCourse(semesterCourseCode));
  };

  const onGetLessonPlans = (_timetableSeq: number) => {
    if (courseIndex !== 0 && !courseIndex) return;
    const semesterCourseCode = courseInput[courseIndex].id;
    execute3(getLessonPlans(semesterCourseCode, _timetableSeq));
  };

  const onSetPlanInputs = (index: number, key: string, value: any) => {
    const inputs = [...planInputs];
    inputs[index][key] = value;
    setPlanInputs(inputs);
  };

  const onSubmit = () => {
    if (courseIndex !== 0 && !courseIndex) return;
    const semesterCourseCode = courseInput[courseIndex].id;

    // 임시해제
    // for (let i=0; i<planInputs.length; i++) {
    //   if (!planInputs[i].subjectCode) {
    //     return infoNotification('과목을 선택해주세요.');
    //   }
    //   if (!planInputs[i].lessonName) {
    //     return infoNotification('Lesson을 입력해주세요.');
    //   }
    // }

    isCreate
      ? submitExecute(createLessonPlans(semesterCourseCode, planInputs))
      : submitExecute(updateLessonPlans(planInputs));
  };

  const columns: TableColumnsType<ILessonPlan> = [
    {
      title: t('program.semester.column20'),
      dataIndex: 'classTimeSeq',
      key: 'classTimeSeq',
      align: 'center',
    },
    {
      title: t('program.semester.column21'),
      dataIndex: 'subjectCode',
      key: 'subjectCode',
      align: 'center',
      render: (value, record, index) => (
        <Select
          className="w-100"
          placeholder={t('common.button.select')}
          value={value}
          onChange={(value) => onSetPlanInputs(index, 'subjectCode', value)}
        >
          {res4?.data.map((item: any) => {
            return (
              <Select.Option key={item.subjectCode} value={item.subjectCode}>
                {item.subjectName}
              </Select.Option>
            );
          })}
        </Select>
      ),
    },
    {
      title: 'Lesson',
      dataIndex: 'lessonName',
      key: 'lessonName',
      align: 'center',
      render: (value, record, index) => {
        return (
          <input
            className="form-control"
            placeholder={t('common.text.input')}
            value={value ? value : ''}
            onChange={(e) =>
              onSetPlanInputs(index, 'lessonName', e.target.value)
            }
          />
        );
      },
    },
  ];

  return (
    <>
      {(isLoading || isLoading2 || isLoading3 || isLoading4 || submitIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={error4} />
      <ErrorResponse error={submitError} />

      <div className="flex flex-justify-end mb-3">
        <div className="w-150p mr-1">
          <Select
            className="w-100"
            placeholder={t('program.semester.placeholder1')}
            value={courseIndex}
            onChange={(value) => onSelectCourse(value)}
          >
            {chosenCourseList?.map((item: any) => {
              return (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              );
            })}
          </Select>
        </div>
      </div>
      {courseIndex === 0 || courseIndex ? (
        <div className="flex">
          {/* left side */}
          <div className="col-4">
            <div>
              <div className="card">
                <ScrollView className="card-body">
                  <table className="table table-bordered table-basic">
                    <colgroup>
                      <col width="50%" />
                      <col width="50%" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="text-center">{t('program.semester.column22')}</th>
                        <th className="text-center">{t('program.semester.column23')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courseTimesList.map((item: any, index: number) => (
                        <tr
                          key={index}
                          className={
                            timetableSeq === item.timetableSeq
                              ? 'chosen'
                              : 'pointer'
                          }
                          onClick={() => {
                            onGetLessonPlans(item.timetableSeq);
                            setTimetableSeq(item.timetableSeq);
                          }}
                        >
                          <td className="text-center">{item.month}</td>
                          <td className="text-center">{item.times}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollView>
              </div>
            </div>
          </div>

          {/* right side */}
          {timetableSeq ? (
            <div className="col-8">
              <div className="card">
                <div className="card-body">
                  <Table
                    columns={columns}
                    dataSource={planInputs}
                    pagination={{
                      pageSize: 99999,
                    }}
                    rowKey="classTimeSeq"
                  />
                  <div className="text-center mt-4">
                    <Button
                      onClick={onSubmit}
                      className="btn btn-sm btn-primary btn-w100p mr-1"
                      text={t('common.button.save')}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
};

export default SemesterPlan;
